import React from 'react';
import {
  Box,
  TextField,
  Switch,
  Button,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import {
  ControlModeTemplateFieldsType,
  ControlModeTemplateSettings,
} from '@src/types/ControlModes';
import { inputStyles } from '../styles';
import { useFields } from '../../hooks/useFields';
import { useFormContext } from '../../context/FormContext';
import SelectFieldFormItem from './SelectFieldFormItem';
import useAlias from '@src/hooks/useAlias';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';

interface FieldFormProps {
  pendingSetting?: ControlModeTemplateSettings | null;
}

const FieldForm: React.FC<FieldFormProps> = ({ pendingSetting = null }) => {
  const alias = useAlias();
  const { state } = useFormContext();
  const {
    isEditMode,
    editingField,
    selectedSettingIndex,
    setSelectedSettingIndex,
    newFieldData,
    handleNewFieldChange,
    handleFieldTypeChange,
    handleNewFieldToggle,
    newFieldErrors,
    parsedOptions,
    newOption,
    handleAddOption,
    handleRemoveOption,
    handleNewRangeChange,
    addNewField,
    setNewOption,
    resetFieldForm,
  } = useFields();
  return (
    <Grid2
      container
      spacing={2}
      size={12}
      sx={{
        mt: 3,
        p: 2,
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        borderRadius: '4px',
      }}
    >
      <Grid2 size={12}>
        <Typography variant='h6' gutterBottom>
          {isEditMode
            ? alias('Edit Field "{uiLabel}"', {
                uiLabel: editingField?.uiLabel ?? '',
              })
            : pendingSetting
              ? alias('Add Field to New Setting "{uiLabel}"', {
                  uiLabel: pendingSetting.uiLabel,
                })
              : alias('Add New Field')}
        </Typography>
        {pendingSetting && !isEditMode && (
          <Typography variant='body2' gutterBottom>
            {alias('You must add at least one field to create this setting')}
          </Typography>
        )}
      </Grid2>

      {state.unsavedSettings && state.unsavedSettings.length > 0 && (
        <Grid2 size={12} sx={{ mb: 2 }}>
          <FormControl
            fullWidth
            variant='outlined'
            size='small'
            sx={inputStyles}
          >
            <Typography variant='body2' gutterBottom>
              {alias('Add to Setting')}
            </Typography>
            <Select
              value={selectedSettingIndex}
              onChange={(e) => setSelectedSettingIndex(Number(e.target.value))}
            >
              {state.unsavedSettings.map(
                (setting: ControlModeTemplateSettings, index: number) => (
                  <MenuItem
                    key={`setting-${index}-${setting.controllerField}`}
                    value={index}
                  >
                    {setting.uiLabel}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        </Grid2>
      )}

      <Grid2 size={6}>
        <TextField
          fullWidth
          label={alias('Field Label')}
          name='uiLabel'
          value={newFieldData.uiLabel}
          onChange={handleNewFieldChange}
          variant='outlined'
          size='small'
          error={!!newFieldErrors['uiLabel']}
          helperText={newFieldErrors['uiLabel']}
          sx={inputStyles}
        />
      </Grid2>

      <Grid2 size={6}>
        <TextField
          fullWidth
          label={alias('Controller Field')}
          name='controllerField'
          value={newFieldData.controllerField}
          onChange={handleNewFieldChange}
          variant='outlined'
          size='small'
          error={!!newFieldErrors['controllerField']}
          helperText={newFieldErrors['controllerField']}
          sx={inputStyles}
        />
      </Grid2>

      <Grid2 size={6}>
        <FormControl fullWidth variant='outlined' size='small' sx={inputStyles}>
          <Typography variant='body2' gutterBottom>
            {alias('Field Type')}
          </Typography>
          <Select
            name='type'
            value={newFieldData.type}
            onChange={handleFieldTypeChange}
          >
            <MenuItem value={ControlModeTemplateFieldsType.STRING}>
              <FormattedAlias id='String' />
            </MenuItem>
            <MenuItem value={ControlModeTemplateFieldsType.NUMBER}>
              <FormattedAlias id='Number' />
            </MenuItem>
            <MenuItem value={ControlModeTemplateFieldsType.BOOLEAN}>
              <FormattedAlias id='Boolean' />
            </MenuItem>
            <MenuItem value={ControlModeTemplateFieldsType.SELECT}>
              <FormattedAlias id='Select' />
            </MenuItem>
          </Select>
        </FormControl>
      </Grid2>

      <Grid2 size={6}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Typography variant='body2' sx={{ mb: 1 }}>
            {alias('Quick Config')}
          </Typography>
          <Switch
            name='quickConfig'
            checked={newFieldData.quickConfig}
            onChange={handleNewFieldToggle}
          />
        </Box>
      </Grid2>

      {/* Conditional fields based on type */}
      {newFieldData.type === ControlModeTemplateFieldsType.STRING && (
        <Grid2 size={12}>
          <TextField
            fullWidth
            label={alias('Default Value')}
            name='defaultValue'
            value={newFieldData.defaultValue}
            onChange={handleNewFieldChange}
            variant='outlined'
            size='small'
            error={!!newFieldErrors['defaultValue']}
            helperText={newFieldErrors['defaultValue']}
            sx={inputStyles}
          />
        </Grid2>
      )}

      {newFieldData.type === ControlModeTemplateFieldsType.NUMBER && (
        <>
          <Grid2 size={6}>
            <TextField
              fullWidth
              label={alias('Default Value')}
              name='defaultValue'
              value={newFieldData.defaultValue}
              onChange={handleNewFieldChange}
              type='number'
              variant='outlined'
              size='small'
              error={!!newFieldErrors['defaultValue']}
              helperText={newFieldErrors['defaultValue']}
              sx={inputStyles}
            />
          </Grid2>
          <Grid2 size={3}>
            <TextField
              fullWidth
              label={alias('Min')}
              name='rangeMin'
              value={newFieldData.range?.min ?? ''}
              onChange={handleNewRangeChange}
              type='number'
              variant='outlined'
              size='small'
              sx={inputStyles}
            />
          </Grid2>
          <Grid2 size={3}>
            <TextField
              fullWidth
              label={alias('Max')}
              name='rangeMax'
              value={newFieldData.range?.max ?? ''}
              onChange={handleNewRangeChange}
              type='number'
              variant='outlined'
              size='small'
              sx={inputStyles}
            />
          </Grid2>
        </>
      )}

      {newFieldData.type === ControlModeTemplateFieldsType.BOOLEAN && (
        <Grid2 size={12}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant='body2' mr={2}>
                {alias('Default Value')}
              </Typography>
              <Switch
                name='defaultValue'
                checked={newFieldData.defaultValue === 'true'}
                onChange={(e) => {
                  handleNewFieldChange({
                    target: {
                      name: 'defaultValue',
                      value: e.target.checked ? 'true' : 'false',
                    },
                  } as React.ChangeEvent<HTMLInputElement>);
                }}
                color={newFieldErrors['defaultValue'] ? 'error' : 'primary'}
              />
            </Box>
            {!!newFieldErrors['defaultValue'] && (
              <Typography variant='caption' color='error' sx={{ mt: 1 }}>
                {newFieldErrors['defaultValue']}
              </Typography>
            )}
          </Box>
        </Grid2>
      )}

      {newFieldData.type === ControlModeTemplateFieldsType.SELECT && (
        <SelectFieldFormItem
          newOption={newOption}
          parsedOptions={parsedOptions}
          newFieldData={newFieldData}
          newFieldErrors={newFieldErrors}
          setNewOption={setNewOption}
          handleAddOption={handleAddOption}
          handleRemoveOption={handleRemoveOption}
          handleNewFieldChange={handleNewFieldChange}
        />
      )}

      <Grid2 size={12} sx={{ mt: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant='outlined'
            color='primary'
            onClick={resetFieldForm}
            disabled={pendingSetting !== null}
            title={
              pendingSetting
                ? alias('You must add a field to create this setting')
                : ''
            }
          >
            <FormattedAlias id='Cancel' />
          </Button>
          <Button variant='contained' color='primary' onClick={addNewField}>
            {isEditMode ? (
              <FormattedAlias id='Save' />
            ) : (
              <FormattedAlias id='Add Field' />
            )}
          </Button>
        </Box>
      </Grid2>
    </Grid2>
  );
};

export default FieldForm;
